/**
 * Compact relative-time formatters. Different output styles exist across admin
 * ("5m" vs "5m ago" vs "5 minutes ago"); consolidate further only when the
 * styles converge. Add a style here rather than in a feature folder.
 */

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * Compact "age" label: `5m`, `3h`, `2d`. Returns `'New'` for future timestamps
 * (clock skew tolerance) and `'-'` for missing / unparseable input. Used in
 * data tables to indicate how old a row is at a glance.
 */
export const formatAge = (servicedAt: string | Date | undefined | null): string => {
    if (!servicedAt) return '-'
    try {
        const d = servicedAt instanceof Date ? servicedAt : new Date(servicedAt)
        if (isNaN(d.getTime())) return '-'
        const diffMs = Date.now() - d.getTime()
        if (diffMs < 0) return 'New'
        const mins = Math.floor(diffMs / 60000)
        if (mins < 60) return `${mins}m`
        const hours = Math.floor(mins / 60)
        if (hours < 24) return `${hours}h`
        const days = Math.floor(hours / 24)
        return `${days}d`
    } catch {
        return '-'
    }
}

/**
 * Verbose relative label: `just now`, `5 minutes ago`, `3 hours ago`, `2 days ago`.
 * Returns `'just now'` for future timestamps (clock skew) and `''` for missing input.
 */
export const formatTimeAgo = (value: string | Date | undefined | null): string => {
    if (!value) return ''
    try {
        const d = value instanceof Date ? value : new Date(value)
        if (isNaN(d.getTime())) return ''
        const diffMs = Date.now() - d.getTime()
        if (diffMs < 60000) return 'just now'
        const mins = Math.floor(diffMs / 60000)
        if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
        const hours = Math.floor(mins / 60)
        if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
        const days = Math.floor(hours / 24)
        return `${days} day${days === 1 ? '' : 's'} ago`
    } catch {
        return ''
    }
}

/**
 * Compact age with a suffix — "just now" / "12m ago" / "2h ago" / "3d ago", falling back to an absolute
 * date past a week. Deliberately coarse: answers "how fresh is this?", and unlike a plain date it visibly
 * changes when the thing runs twice on the same day. `now` is injectable so a caller re-rendering on a
 * timer (and its tests) can pin the reference point.
 */
export const formatSyncAge = (date: Date, now: Date = new Date()): string => {
    const diff = now.getTime() - date.getTime()
    if (diff < MINUTE) return 'just now'
    if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`
    if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`
    if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}d ago`
    // Keep the year when it isn't the current one, so a stamp from last July doesn't read as this July.
    const sameYear = date.getFullYear() === now.getFullYear()
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' })
}

/**
 * Spelled-out duration for prose — "just now", "8 min", "1 hr", "2 hrs 5 min". Unlike the labels above
 * this one carries no "ago" suffix, so it reads correctly mid-sentence ("delayed for 2 hrs 5 min").
 * Negative input clamps to `just now` rather than counting backwards through clock skew; a non-finite
 * input is reported as `unknown` rather than silently reading as a fresh timestamp.
 *
 * The `Ms` suffix is load-bearing. `formatDuration` is defined six times across admin and none of them
 * agree on a unit: three take **hours** (`ongoing-loads/utils/helpers.ts`, `shared/LoadsTable/
 * MapAndCosts/CostsCard.tsx`, `features/load-hub/.../CostsBreakdown.tsx`), two take a
 * `(startedAt, endedAt)` pair (`features/agentic-hub/.../LoadDetailPanel.tsx`,
 * `layouts/.../AICallHistory.tsx`), and one takes milliseconds like this one
 * (`features/agentic-hub/.../LoadNoteButton.tsx`). The hours ones are `number → string` exactly as this
 * is, so an auto-import from the wrong module compiles clean and is wrong by 3.6M×.
 */
export const formatDurationMs = (ms: number): string => {
    if (!Number.isFinite(ms)) return 'unknown'

    const totalMinutes = Math.max(0, Math.floor(ms / MINUTE))
    if (totalMinutes === 0) return 'just now'
    if (totalMinutes < 60) return `${totalMinutes} min`

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    const hourLabel = hours === 1 ? 'hr' : 'hrs'
    return minutes > 0 ? `${hours} ${hourLabel} ${minutes} min` : `${hours} ${hourLabel}`
}

/**
 * Compact elapsed label for a badge: `12m`, `3h 05m`, `2d 4h`. Zero-padded minutes so the width stays
 * steady as it ticks, and it rolls over to days — a load waiting 20 minutes and one waiting two days
 * must not read the same.
 *
 * Pick this over `formatDurationMs` when the label sits inside a pill and every character costs width;
 * `formatDurationMs` ("2 hrs 5 min") is the prose form for sentences and tooltips, and never rolls over
 * to days. Pick `formatAge` when a single unit is enough.
 */
export const formatElapsedMs = (elapsedMs: number): string => {
    const totalMinutes = Math.max(0, Math.floor(elapsedMs / MINUTE))
    if (totalMinutes < 60) return `${totalMinutes}m`

    const hours = Math.floor(totalMinutes / 60)
    if (hours < 24) return `${hours}h ${String(totalMinutes % 60).padStart(2, '0')}m`

    return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

/** Full timestamp to pair with a relative label on hover — "Jul 25, 2026, 2:14 PM". */
export const formatSyncTimestamp = (date: Date): string =>
    date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
